import {Breadcrumb} from '@/components/atoms'
import {PageHeader, TabsComponent} from '@/components/molecules'
import {BaseDataEdit, BrandAndCategoryEdit, MediaGalleryEdit, ProductDisplayEdit} from '@/containers'
import {TaxAndSalesEdit} from '@/containers/ProductReviewEditContainer/TaxAndSales/TaxAndSalesEdit'
import {useAzureBlob} from '@/hooks/useAzureBlob-hook'
import {useMediaUpload} from '@/hooks/useMediaUpload'
import {updateProduct} from '@/services/ProductsService/product'
import {getSastoken, updateProductMediaMetadata} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {ROUTES} from '@/utils/constants/routesConstants'
import {convertEmptyStringsToNull} from '@/utils/objectUtils'
import {useQueryClient} from '@tanstack/react-query'
import {useEffect, useMemo, useState} from 'react'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {useProductOverview} from './ProductReview/useProductOverview'

const DEFAULT_MEDIA_FILE_SIZE = 52428800

const buildSasTokenMediaPayload = mediaSlots => {
  const media = mediaSlots
    .filter(slot => slot?.file && slot?.metadata)
    .map(slot => ({
      fileName: slot.file?.name || '',
      mediaType: slot.metadata?.mediaType || 'IMAGE',
      displayOrder: Number(slot.displayOrder),
      fileSize:
        slot.file?.size || slot.metadata?.blobFileSize || slot.metadata?.originalSize || DEFAULT_MEDIA_FILE_SIZE,
    }))
    .filter(item => item.fileName)

  return {media}
}

const findMatchingMediaSlot = (slotsWithFiles, sasMedia, index) => {
  return (
    slotsWithFiles.find(slot => Number(slot.displayOrder) === Number(sasMedia?.displayOrder)) || slotsWithFiles[index]
  )
}

const mapSasTokenResponseToProductMedia = (sasMediaItems, mediaSlots) => {
  const slotsWithFiles = mediaSlots.filter(slot => slot?.file && slot?.metadata)

  return (sasMediaItems || [])
    .map((sasMedia, index) => {
      const matchingSlot = findMatchingMediaSlot(slotsWithFiles, sasMedia, index)

      if (!sasMedia?.cdnUrl || !matchingSlot) {
        return null
      }

      return {
        mediaUrl: sasMedia.cdnUrl,
        displayOrder: Number(sasMedia?.displayOrder ?? matchingSlot.displayOrder ?? index + 1),
        mediaType: matchingSlot.metadata?.mediaType || 'IMAGE',
        mediaTitle: sasMedia.fileName || matchingSlot.metadata?.mediaTitle || matchingSlot.file?.name || '',
      }
    })
    .filter(Boolean)
}

const mapExistingSlotsToProductMedia = mediaSlots =>
  (mediaSlots || [])
    .filter(slot => !slot?.file && slot?.imageUri)
    .map(slot => ({
      mediaUrl: slot.imageUri,
      displayOrder: Number(slot.displayOrder),
      mediaType: slot?.metadata?.mediaType || 'IMAGE',
      mediaTitle: slot?.metadata?.mediaTitle || slot?.metadata?.originalFileName || '',
    }))
    .sort((left, right) => Number(left.displayOrder) - Number(right.displayOrder))

const normalizeMediaForComparison = mediaList =>
  (mediaList || [])
    .map(item => ({
      mediaUrl: item?.mediaUrl || '',
      displayOrder: Number(item?.displayOrder),
      mediaType: item?.mediaType || 'IMAGE',
      mediaTitle: item?.mediaTitle || '',
    }))
    .sort((left, right) => Number(left.displayOrder) - Number(right.displayOrder))

const hasMediaChanged = (nextMedia, currentMedia) => {
  const normalizedNext = normalizeMediaForComparison(nextMedia)
  const normalizedCurrent = normalizeMediaForComparison(currentMedia)

  if (normalizedNext.length !== normalizedCurrent.length) return true

  return normalizedNext.some((item, index) => {
    const current = normalizedCurrent[index]
    return (
      item.displayOrder !== current?.displayOrder ||
      item.mediaUrl !== current?.mediaUrl ||
      item.mediaType !== current?.mediaType ||
      item.mediaTitle !== current?.mediaTitle
    )
  })
}

const mergeUploadedMedia = (existingMedia, uploadedMedia) => {
  if (uploadedMedia.length === 0) return existingMedia

  const uploadedMediaByDisplayOrder = new Map(uploadedMedia.map(media => [Number(media.displayOrder), media]))
  const retainedMedia = (existingMedia || []).filter(
    media => !uploadedMediaByDisplayOrder.has(Number(media?.displayOrder))
  )

  return [...retainedMedia, ...uploadedMedia].sort(
    (left, right) => Number(left.displayOrder) - Number(right.displayOrder)
  )
}

const mapFilesForBlobUpload = (sasMediaItems, mediaSlots) => {
  const slotsWithFiles = mediaSlots.filter(slot => slot?.file)

  return (sasMediaItems || [])
    .map((sasMedia, index) => {
      const matchingSlot = findMatchingMediaSlot(slotsWithFiles, sasMedia, index)

      if (!matchingSlot?.file || !sasMedia?.sasToken || !sasMedia?.directory) {
        return null
      }

      const renamedFileName = sasMedia?.fileName || matchingSlot.file.name
      const fileToUpload =
        renamedFileName !== matchingSlot.file.name
          ? new File([matchingSlot.file], renamedFileName, {
              type: matchingSlot.file.type,
              lastModified: matchingSlot.file.lastModified,
            })
          : matchingSlot.file

      return {
        sasToken: sasMedia.sasToken,
        directory: sasMedia.directory,
        file: fileToUpload,
      }
    })
    .filter(Boolean)
}

const groupUploadsByAuthContext = preparedFiles => {
  const grouped = new Map()

  preparedFiles.forEach(item => {
    const key = `${item.sasToken}::${item.directory}`
    const existing = grouped.get(key)

    if (existing) {
      existing.files.push(item.file)
    } else {
      grouped.set(key, {
        sasToken: item.sasToken,
        directory: item.directory,
        files: [item.file],
      })
    }
  })

  return [...grouped.values()]
}

const getProductMediaRequestContext = productData => {
  const countryCode = productData?.additionalInfo?.countryCode || productData?.countryCode
  const isColombia = countryCode === 'CO'

  return {
    countryCode,
    ...(isColombia && {salesOrg: productData?.additionalInfo?.salesOrg || productData?.salesOrg}),
  }
}

export const ProductReviewEdit = () => {
  const {id} = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const {uploadMediaFilesOnBlob} = useAzureBlob()

  const {productOverviewData} = useProductOverview()
  const [formData, setFormData] = useState({})
  const existingProductMedia = useMemo(
    () => (Array.isArray(productOverviewData?.data?.media) ? productOverviewData.data.media : []),
    [productOverviewData?.data?.media]
  )

  const {mediaSlots, isUploading, updateMediaSlot, clearMediaSlot} = useMediaUpload(id, existingProductMedia)

  const location = useLocation()
  const state = location?.state

  useEffect(() => {
    if (productOverviewData?.data) {
      setFormData(productOverviewData.data)
    }
  }, [productOverviewData])

  const handleFormChange = updatedData => {
    setFormData(prevData => {
      const newData = {...prevData, ...updatedData}
      return newData
    })
  }
  const handleSaveProduct = async dataToSave => {
    try {
      const hasMediaFilesToUpload = mediaSlots.some(slot => slot.file && slot.metadata)
      const currentData = dataToSave || formData
      let dataToSubmit = currentData
      const productIdForMedia = id || dataToSubmit?.id || dataToSubmit?.productId
      const hasSlotMedia = mediaSlots.some(slot => slot?.file || slot?.imageUri)
      const existingMediaFromSlots = hasSlotMedia
        ? mapExistingSlotsToProductMedia(mediaSlots)
        : dataToSubmit?.media || []
      const hasExistingMediaChanges = hasSlotMedia && hasMediaChanged(existingMediaFromSlots, currentData?.media || [])

      if (hasSlotMedia) {
        dataToSubmit = {
          ...dataToSubmit,
          media: existingMediaFromSlots,
        }
      }

      if (hasMediaFilesToUpload && productIdForMedia) {
        const mediaPayload = buildSasTokenMediaPayload(mediaSlots)

        if (mediaPayload.media.length > 0) {
          const sastokenResponse = await getSastoken({
            media: mediaPayload.media,
            countryCode: dataToSubmit?.additionalInfo?.countryCode || dataToSubmit?.countryCode,
            salesOrg: dataToSubmit?.additionalInfo?.salesOrg || dataToSubmit?.salesOrg,
          })
          const sasMediaItems = Array.isArray(sastokenResponse?.media)
            ? sastokenResponse.media
            : Array.isArray(sastokenResponse?.data?.media)
              ? sastokenResponse.data.media
              : []

          const preparedFiles = mapFilesForBlobUpload(sasMediaItems, mediaSlots)
          const uploadGroups = groupUploadsByAuthContext(preparedFiles)

          for (const uploadGroup of uploadGroups) {
            const blobUploadResults = await uploadMediaFilesOnBlob(
              {sasToken: uploadGroup.sasToken, directory: uploadGroup.directory},
              uploadGroup.files
            )

            const hasBlobUploadError = blobUploadResults.some(result => !result.success)
            if (hasBlobUploadError) {
              throw new Error('Failed to upload one or more media files to blob storage')
            }
          }

          const uploadedMedia = mapSasTokenResponseToProductMedia(sasMediaItems, mediaSlots)
          dataToSubmit = {
            ...dataToSubmit,
            media: mergeUploadedMedia(dataToSubmit?.media, uploadedMedia),
          }
        }
      }
      const sanitizedData = convertEmptyStringsToNull(dataToSubmit)

      // If media is an empty array, remove it from the payload
      if (Array.isArray(sanitizedData?.media) && sanitizedData.media.length === 0) {
        delete sanitizedData.media
      }

      const updateProductRequest = updateProduct(sanitizedData)
      const mediaRequestContext = getProductMediaRequestContext(sanitizedData)
      const shouldUpdateMediaMetadata = hasMediaFilesToUpload || hasExistingMediaChanges
      const uploadMediaRequest = shouldUpdateMediaMetadata
        ? updateProductMediaMetadata({
            productId: productIdForMedia,
            media: sanitizedData?.media || [],
            ...mediaRequestContext,
          }).then(() => true)
        : Promise.resolve(true)

      const [response, mediaUploadSucceeded] = await Promise.all([updateProductRequest, uploadMediaRequest])

      if (!mediaUploadSucceeded) return response

      const targetId = id || response?.id || response?.productId
      if (targetId) {
        // Invalidate both the product list and the individual product queries
        await queryClient.invalidateQueries(['PRODUCT_LIST'])
        await queryClient.invalidateQueries(['PRODUCT', targetId])

        navigate(ROUTES.PRODUCTS_LIST)
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Product Successfully Updated',
            type: 'success',
          })
        )
      } else {
        console.warn('Could not determine ID for redirection after save.')
      }

      return response
    } catch (error) {
      dispatch(
        setToastNotifications({
          show: true,
          message: `${error}`,
          type: 'error',
        })
      )
      throw error
    }
  }

  if (!formData || Object.keys(formData).length === 0) {
    return <div>Loading ...</div>
  }

  const productName = formData.name || formData.productName || formData.baseData?.name || 'Loading...'
  const tabList = [
    {
      id: 'Base Data',
      tabLabel: 'Base Data',
      tabComponent: <BaseDataEdit formData={formData} onDataChange={handleFormChange} onPublish={handleSaveProduct} />,
    },
    {
      id: 'Media',
      tabLabel: 'Media',
      tabComponent: (
        <MediaGalleryEdit
          mediaSlots={mediaSlots}
          isUploading={isUploading}
          updateMediaSlot={updateMediaSlot}
          clearMediaSlot={clearMediaSlot}
          handleSave={handleSaveProduct}
        />
      ),
    },
    {
      id: 'Display',
      tabLabel: 'Display',
      tabComponent: (
        <ProductDisplayEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Brand & Category',
      tabLabel: 'Brand & Category',
      tabComponent: (
        <BrandAndCategoryEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
    {
      id: 'Tax & Sales ',
      tabLabel: 'Tax & Sales ',
      tabComponent: (
        <TaxAndSalesEdit formData={formData} setFormData={handleFormChange} handleSave={handleSaveProduct} />
      ),
    },
  ]

  const breadcrumbs = [
    {href: '/', label: ''},
    {href: ROUTES.PRODUCTS_LIST, label: 'Product view'},
    {href: '', label: productName},
  ]

  return (
    <>
      <Breadcrumb uiSchema={{isDark: true, className: 'mb-5', breadcrumbs}} id="productOverviewBreadcrumb" />
      <div className="mb-4 flex items-center justify-between">
        <PageHeader title={productName} />
      </div>
      <TabsComponent
        isDarkTab={false}
        isBorderVisible={false}
        tabList={tabList}
        defaultTabIndex={state?.selectedTabNumb ?? 0}
        removeBG={true}
      />
    </>
  )
}

export default ProductReviewEdit
